import React, { useState } from 'react';
import { Clapperboard, Play } from 'lucide-react';
import { useTheme } from 'components/core/theme-provider';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'components/ui/skeleton';

interface SearchRelatedVideoListProps {
    videoList: any;
    messageId?: string;
    isSkeletonShowForVideos?: boolean;
}

export interface RelativeSearchVideo {
    title: string;
    imgSrc: string | null;
    url: string | null;
    iframeSrc: string | null;
}

export default function SearchRelatedVideoList({ videoList, messageId, isSkeletonShowForVideos }: SearchRelatedVideoListProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [loadedVideos, setLoadedVideos] = useState<Set<string>>(new Set());
    const { t } = useTranslation();
    const { theme } = useTheme();

    const cardClass =
        theme === 'dark'
            ? "bg-white-25 hover:bg-primary-600 group transition duration-200 rounded-lg p-2 flex-col space-y-2 font-medium cursor-pointer"
            : "bg-gray-100 hover:bg-gray-200 transition duration-200 rounded-lg p-2 flex-col space-y-2 font-medium cursor-pointer";

    const videosList = videoList?.data || [];
    const remainingCount = Math.max(videosList.length - 3, 0);


    const getVideoKey = (index: number) => `${messageId}-${index}`;

    const handleVideoLoad = (index: number) => {
        const key = getVideoKey(index);
        setLoadedVideos(prev => new Set(prev).add(key));
    };

    const isVideoLoaded = (index: number) => {
        return loadedVideos.has(getVideoKey(index));
    };

    const openInNewTab = (url: string | null) => {
        if (!url) return;
        const printWindow = window.open(url, '_blank');
        printWindow?.document.close();
    };

    if (isSkeletonShowForVideos) {
        return (
            <div className="w-full p-4">
                <div className="flex items-center gap-2 mb-4">
                    <Skeleton className="h-5 w-5 rounded" />
                    <Skeleton className="h-6 w-24" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="rounded-lg p-2 flex-col space-y-2">
                            <Skeleton className="w-full h-14 rounded-lg" />
                            <Skeleton className="h-4 w-full" />
                        </div>
                    ))}
                </div>
            </div>
        );
    }


    if (videosList.length) {
        return (
            <div className="w-full p-4">
                {videosList.length ?
                    <div className="flex items-center gap-2 mb-4">
                        <Clapperboard size={20} /> <span className="text-lg fw-500">{t('VIDEOS')}</span>
                    </div> : null}

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {videosList.slice(0, 3).map((video: RelativeSearchVideo, index: number) => (
                        <div
                            key={index}
                            className={cardClass}
                            onClick={() => openInNewTab(video.url)}
                        >
                            <div className="relative w-full h-14 rounded-lg overflow-hidden">
                                {!isVideoLoaded(index) && (
                                    <Skeleton className="absolute inset-0 rounded-lg" />
                                )}
                                <img
                                    src={video.imgSrc || ''}
                                    alt={`Video ${index + 1}`}
                                    className={`w-full h-full object-cover ${isVideoLoaded(index) ? 'opacity-100' : 'opacity-0'
                                        }`}
                                    onLoad={() => handleVideoLoad(index)}
                                />

                                {isVideoLoaded(index) && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                                        <Play className="text-white" size={20} />
                                    </div>
                                )}
                            </div>
                            <p className="text-xs truncate text-center">{video.title}</p>
                        </div>
                    ))}

                    {/* View more button */}
                    {remainingCount > 0 && (
                        <button onClick={() => setIsModalOpen(true)} className={cardClass}>
                            <div className="flex flex-row items-center space-x-1 justify-center">
                                {videosList.slice(3, 6).map((video: RelativeSearchVideo, index: number) => (
                                    <img
                                        key={index}
                                        width="16"
                                        height="16"
                                        alt="favicon"
                                        className="rounded-lg h-4 w-4"
                                        src={video.imgSrc || ''}
                                    />
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 group-hover:text-typography-white">
                                {t("VIEW")} {videosList.length - 3} {t("MORE")}
                            </p>
                        </button>
                    )}
                </div>

                {/* Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div className="bg-white rounded-xl p-6 w-11/12 max-w-3xl shadow-xl overflow-y-auto max-h-[90vh]">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-lg font-semibold">{t('ALL_VIDEOS')}</h2>
                                <button
                                    onClick={() => setIsModalOpen(false)}
                                    className="text-xl hover:opacity-70"
                                >
                                    ✖
                                </button>
                            </div>

                            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                                {videosList.map((video: RelativeSearchVideo, index: number) => (
                                    <div
                                        key={index}
                                        className="relative cursor-pointer group"
                                        onClick={() => openInNewTab(video.url)}
                                    >
                                        <img
                                            src={video.imgSrc || ''}
                                            alt={`Video ${index + 1}`}
                                            className="w-full aspect-square object-cover rounded-lg transition group-hover:opacity-80"
                                        />
                                        {/* Play icon overlay */}
                                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg opacity-0 group-hover:opacity-100 transition">
                                            <Play className="text-white" size={30} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    } else {
        return null;
    }
}